import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { UserTableColumn } from './components/UserTableColumn';
import { useRsi } from '../../hooks/useRsi';
import { TemplateColumn } from './components/TemplateColumn';
import { ColumnGrid } from './components/ColumnGrid';
import { setColumn } from './utils/setColumn';
import { setIgnoreColumn } from './utils/setIgnoreColumn';
import { setSubColumn } from './utils/setSubColumn';
import { normalizeTableData } from './utils/normalizeTableData';
import type { Field } from '../../types';
import { getMatchedColumns } from './utils/getMatchedColumns';
import { UnmatchedFieldsAlert } from '../../components/Alerts/UnmatchedFieldsAlert';
import { findUnmatchedRequiredFields } from './utils/findUnmatchedRequiredFields';
import { type Column, type Columns, ColumnType, type MatchColumnsProps } from './types';

export const MatchColumnsStep = <T extends string>({
  data,
  headerValues,
  onContinue,
  onBack,
  setHasChanged
}: MatchColumnsProps<T>) => {
  const { enqueueSnackbar } = useSnackbar();
  const dataExample = data.slice(0, 2);
  const { autoMapDistance, autoMapHeaders, autoMapSelectValues, fields, matcherIgnorePrefixes, translations } =
    useRsi<T>();
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState<Columns<T>>(
    // Do not remove spread, it indexes empty array elements, otherwise map() skips over them
    ([...headerValues] as string[]).map((value, index) => ({ type: ColumnType.empty, index, header: value ?? '' }))
  );
  const [showUnmatchedFieldsAlert, setShowUnmatchedFieldsAlert] = useState(false);

  const onChange = useCallback(
    (value: T, columnIndex: number) => {
      setHasChanged?.(true);
      const field = fields.find((field) => field.key === value) as unknown as Field<T>;
      if (field) {
        const existingFieldIndex = columns.findIndex((column) => 'value' in column && column.value === field.key);
        setColumns(
          columns.map<Column<T>>((column, index) => {
            columnIndex === index ? setColumn(column, field, data) : column;
            if (columnIndex === index) {
              return setColumn(column, field, data, autoMapSelectValues);
            } else if (index === existingFieldIndex) {
              enqueueSnackbar(
                <>
                  {translations.matchColumnsStep.duplicateColumnWarningTitle}
                  <br />
                  {translations.matchColumnsStep.duplicateColumnWarningDescription}
                </>,
                {
                  variant: 'warning'
                }
              );
              return setColumn(column);
            } else {
              return column;
            }
          })
        );
      } else {
        // Clear column if clear button is clicked
        setColumns(
          columns.map((column, index) => (columnIndex === index ? setColumn(column, undefined, data) : column))
        );
      }
    },
    [
      autoMapSelectValues,
      columns,
      data,
      fields,
      enqueueSnackbar,
      translations.matchColumnsStep.duplicateColumnWarningDescription,
      translations.matchColumnsStep.duplicateColumnWarningTitle,
      setHasChanged
    ]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: include columns to properly update the state of the columns
  const onIgnore = useCallback(
    (columnIndex: number) => {
      setColumns(columns.map((column, index) => (columnIndex === index ? setIgnoreColumn<T>(column) : column)));
    },
    [columns, setColumns]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: include columns to properly update the state of the columns
  const onRevertIgnore = useCallback(
    (columnIndex: number) => {
      setColumns(columns.map((column, index) => (columnIndex === index ? setColumn(column) : column)));
    },
    [columns, setColumns]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip setHasChanged, it causes unwanted side effects of marking the form
  const onSubChange = useCallback(
    (value: string, columnIndex: number, entry: string) => {
      setHasChanged?.(true);
      setColumns(
        columns.map((column, index) =>
          columnIndex === index && 'matchedOptions' in column ? setSubColumn(column, entry, value) : column
        )
      );
    },
    [columns, setColumns]
  );
  const unmatchedRequiredFields = useMemo(() => findUnmatchedRequiredFields(fields, columns), [fields, columns]);

  const handleOnContinue = useCallback(async () => {
    if (unmatchedRequiredFields.length > 0) {
      setShowUnmatchedFieldsAlert(true);
    } else {
      setIsLoading(true);
      await onContinue(normalizeTableData(columns, data, fields), data, columns);
      setIsLoading(false);
    }
  }, [unmatchedRequiredFields.length, onContinue, columns, data, fields]);

  const handleAlertOnContinue = useCallback(async () => {
    setShowUnmatchedFieldsAlert(false);
    setIsLoading(true);
    await onContinue(normalizeTableData(columns, data, fields), data, columns);
    setIsLoading(false);
  }, [onContinue, columns, data, fields]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We only want to run this effect once on mount, auto-mapping columns on initial load. Running it again on columns or fields change causes unwanted side effects of re-mapping already matched columns
  useEffect(() => {
    if (autoMapHeaders) {
      setColumns(getMatchedColumns(columns, fields, data, autoMapDistance, autoMapSelectValues, matcherIgnorePrefixes));
    }
  }, []);

  return (
    <>
      <UnmatchedFieldsAlert
        isOpen={showUnmatchedFieldsAlert}
        onClose={() => setShowUnmatchedFieldsAlert(false)}
        fields={unmatchedRequiredFields}
        onConfirm={handleAlertOnContinue}
      />
      <ColumnGrid
        constrainBox
        columns={columns}
        onContinue={handleOnContinue}
        onBack={onBack}
        isLoading={isLoading}
        userColumn={(column) => (
          <UserTableColumn
            column={column}
            onIgnore={onIgnore}
            onRevertIgnore={onRevertIgnore}
            entries={dataExample.map((row) => row[column.index])}
          />
        )}
        templateColumn={(column) => (
          <TemplateColumn column={column} columns={columns} onChange={onChange} onSubChange={onSubChange} />
        )}
      />
    </>
  );
};
